import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { TrackedAircraft } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectAdsbTrafficUpdatesAltitudeMaxFeet,
    selectAdsbTrafficUpdatesAltitudeMinFeet,
} from "features/overview/slice"
import { useAdsbTracking } from "./useAdsbTracking"

export type AdsbTrafficUpdateEvent = {
    id: string
    kind: "entered" | "exited"
    aircraft: TrackedAircraft
    timestamp: number
}

export const useAdsbTrafficUpdates = (orgId: string | undefined) => {
    const { data, rawAircraft } = useAdsbTracking(orgId)
    const altitudeMin = useAppSelector(selectAdsbTrafficUpdatesAltitudeMinFeet)
    const altitudeMax = useAppSelector(selectAdsbTrafficUpdatesAltitudeMaxFeet)

    const filtered = useMemo(() => {
        if (!data) return data
        return data.filter((a) => a.altitude >= altitudeMin && a.altitude <= altitudeMax)
    }, [data, altitudeMin, altitudeMax])

    const [events, setEvents] = useState<AdsbTrafficUpdateEvent[]>([])
    const previousRawById = useRef<Map<string, TrackedAircraft>>(new Map())
    const previousFilteredIds = useRef<Set<string>>(new Set())
    const hasFetched = useRef(false)

    useEffect(() => {
        if (!rawAircraft || !filtered) return
        const currentRawIds = new Set(rawAircraft.map((a) => a.id))
        const currentFilteredIds = new Set(filtered.map((a) => a.id))

        if (!hasFetched.current) {
            // don't notify on first fetch
            hasFetched.current = true
            previousRawById.current = new Map(rawAircraft.map((a) => [a.id, a]))
            previousFilteredIds.current = currentFilteredIds
            return
        }

        const timestamp = Date.now()
        const entered = rawAircraft
            .filter((a) => !previousRawById.current.has(a.id) && currentFilteredIds.has(a.id))
            .map((aircraft): AdsbTrafficUpdateEvent => ({
                id: `${aircraft.id}-${timestamp}`,
                kind: "entered",
                aircraft,
                timestamp,
            }))
        const exited = [...previousRawById.current.values()]
            .filter((a) => !currentRawIds.has(a.id) && previousFilteredIds.current.has(a.id))
            .map((aircraft): AdsbTrafficUpdateEvent => ({
                id: `${aircraft.id}-${timestamp}`,
                kind: "exited",
                aircraft,
                timestamp,
            }))

        if (entered.length || exited.length) {
            setEvents((prev) =>
                [...entered, ...exited, ...prev].sort((a, b) => a.timestamp - b.timestamp)
            )
        }
        previousRawById.current = new Map(rawAircraft.map((a) => [a.id, a]))
        previousFilteredIds.current = currentFilteredIds
    }, [rawAircraft, filtered])

    const dismissEvent = useCallback((eventToDismiss: AdsbTrafficUpdateEvent) => {
        setEvents((prev) => prev.filter((e) => e !== eventToDismiss))
    }, [])

    return { events, aircraft: filtered, dismissEvent }
}
